import React, { useState } from 'react';
import { Row, Col, FormGroup, Label, FormFeedback, InputGroup } from 'reactstrap';
import { SingleDatePicker } from 'react-dates';
import { useIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';
import { CustomDashboardInput } from '../../../containers';
import { yearListRender, FORMAT_STANDARD_DATE } from '../../../constants/dateFormatting';
import { USER_ACCOUNT_TYPES, USER_GENDER } from '../../../constants/userDefs';
import { PlaceAndZipInput } from '../../../components/PlaceAndZipInput';

export const UserCreatePrivateForm = ({
  inputHandler,
  places,
  loadingPlaces,
  countries,
  loadingCountries,
  sector,
  setSector,
  submitted,
  OIB,
  setOIB,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  dateOfBirth,
  setDateOfBirth,
  sex,
  setSex,
  address,
  setAddress,
  city,
  setCity,
  zip,
  setZip,
  country,
  setCountry,
  passportNumber,
  setPassportNumber,
  governmentIssuedIdNumber,
  setGovernmentIssuedIdNumber,
  HZZONumber,
  setHZZONumber,
  ino,
  setIno,
}) => {
  const { formatMessage } = useIntl();
  const [dateInputFocused, setDateInputFocused] = useState(false);

  return (
    <>
      <Row>
        <Col>
          <h3>
            <FormattedMessage id="adminCreateUserPage.basicInfo" /> *
          </h3>
        </Col>
      </Row>
      <FormGroup row className="mt-3">
        <Col xs="12" sm="6">
          <Label for="sector">
            <FormattedMessage id="inputs.sector" />
          </Label>
          <InputGroup>
            <div className="hok-select">
              <CustomDashboardInput
                className="text-uppercase"
                type="select"
                id="sector"
                fieldState={sector}
                onChange={event => inputHandler(setSector, event.target.value)}>
                <option disabled value=""></option>
                {USER_ACCOUNT_TYPES.map(type => (
                  <option key={type.name} value={type.name}>
                    {formatMessage({ id: `adminCreateUserPage.filter.${type.name}` })}
                  </option>
                ))}
              </CustomDashboardInput>
            </div>
          </InputGroup>
        </Col>
        <Col xs="12" sm="6">
          <Label for="OIB">
            <FormattedMessage id="inputs.OIB" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="OIB"
              fieldState={OIB}
              onChange={event => inputHandler(setOIB, event.target.value)}
            />
          </InputGroup>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col xs="12" sm="6">
          <Label for="firstName">
            <FormattedMessage id="inputs.firstName" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="firstName"
              fieldState={firstName}
              onChange={event => inputHandler(setFirstName, event.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs="12" sm="6">
          <Label for="lastName">
            <FormattedMessage id="inputs.lastName" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="lastName"
              fieldState={lastName}
              onChange={event => inputHandler(setLastName, event.target.value)}
            />
          </InputGroup>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col xs="12" sm="6">
          <Label for="dateOfBirth">
            <FormattedMessage id="inputs.dateOfBirth" />
          </Label>
          <InputGroup className={dateOfBirth.invalid && submitted ? 'invalid-date' : ''}>
            <SingleDatePicker
              id="dateOfBirth"
              hideKeyboardShortcutsPanel
              date={dateOfBirth.value}
              onDateChange={date => setDateOfBirth({ ...dateOfBirth, value: date, invalid: false, error: null })}
              showDefaultInputIcon
              firstDayOfWeek={1}
              numberOfMonths={1}
              small
              focused={dateInputFocused}
              onFocusChange={({ focused }) => setDateInputFocused(focused)}
              displayFormat={FORMAT_STANDARD_DATE}
              placeholder=""
              isOutsideRange={date => date.isAfter(moment().subtract(18, 'year'), 'day')}
              initialVisibleMonth={() => dateOfBirth.value || moment().subtract(18, 'year')}
              renderMonthElement={({ month, onMonthSelect, onYearSelect }) => (
                <div className="d-flex justify-content-center">
                  <div className="mr-1">
                    <select value={month.month()} onChange={e => onMonthSelect(month, e.target.value)}>
                      {moment.months().map((label, value) => (
                        <option value={value} key={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ml-1">
                    <select value={month.year()} onChange={e => onYearSelect(month, e.target.value)}>
                      {yearListRender()}
                    </select>
                  </div>
                </div>
              )}
            />
            {dateOfBirth.invalid && submitted && <FormFeedback className="d-block">{dateOfBirth.error}</FormFeedback>}
          </InputGroup>
        </Col>
        <Col xs="12" sm="6">
          <Label for="sex">
            <FormattedMessage id="inputs.sex" />
          </Label>
          <InputGroup>
            <div className="hok-select">
              <CustomDashboardInput
                type="select"
                fieldState={sex}
                onChange={event => inputHandler(setSex, event.target.value)}>
                <option disabled value="">
                  {formatMessage({ id: 'inputs.sex' })}
                </option>
                {USER_GENDER.map(sex => (
                  <option key={sex.value} value={sex.value}>
                    {formatMessage({ id: sex.name })}
                  </option>
                ))}
              </CustomDashboardInput>
            </div>
          </InputGroup>
        </Col>
      </FormGroup>
      <FormGroup row className="mb-5">
        <Col xs="12" sm="6">
          <Label for="address">
            <FormattedMessage id="inputs.address" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="address"
              fieldState={address}
              onChange={event => inputHandler(setAddress, event.target.value)}
            />
          </InputGroup>
        </Col>
        <PlaceAndZipInput
          places={places}
          loadingPlaces={loadingPlaces}
          zip={zip}
          setZip={setZip}
          city={city}
          setCity={setCity}
        />
        <Col sm="6">
          <Label for="country">
            <FormattedMessage id="inputs.country" />
          </Label>
          <InputGroup>
            <div className="hok-select">
              <CustomDashboardInput
                id="country"
                type="select"
                value={country.value}
                fieldState={country}
                formSubmitted={submitted}
                disabled
                // onChange={event => inputHandler(setCountry, event.target.value)}
              >
                <option disabled value="">
                  {formatMessage({
                    id: 'inputs.country',
                  })}
                </option>
                {(loadingCountries || !countries) && <option disabled>{country.value}</option>}
                {loadingCountries ? (
                  <option disabled value="">
                    {formatMessage({ id: 'loading' })}
                  </option>
                ) : (
                  countries.map(country => (
                    <option key={country.codeLegacy} value={country.codeLegacy}>
                      {country.nameCro}
                    </option>
                  ))
                )}
              </CustomDashboardInput>
            </div>
          </InputGroup>
        </Col>
      </FormGroup>
      <Row>
        <Col>
          <h3>
            <FormattedMessage id="adminCreateUserPage.advancedInfo" />
          </h3>
        </Col>
      </Row>
      <FormGroup row className="mt-3">
        <Col xs="12" sm="6">
          <Label for="passportNumber">
            <FormattedMessage id="inputs.passportNumber" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="passportNumber"
              fieldState={passportNumber}
              onChange={event => inputHandler(setPassportNumber, event.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs="12" sm="6">
          <Label for="governmentIssuedIdNumber">
            <FormattedMessage id="inputs.governmentIssuedIdNumber" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="governmentIssuedIdNumber"
              fieldState={governmentIssuedIdNumber}
              onChange={event => inputHandler(setGovernmentIssuedIdNumber, event.target.value)}
            />
          </InputGroup>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col xs="12" sm="6">
          <Label for="HZZONumber">
            <FormattedMessage id="inputs.HZZONumber" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="HZZONumber"
              fieldState={HZZONumber}
              onChange={event => inputHandler(setHZZONumber, event.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs="12" sm="6">
          <Label for="ino">
            <FormattedMessage id="inputs.ino" />
          </Label>
          <InputGroup>
            <CustomDashboardInput
              id="ino"
              fieldState={ino}
              onChange={event => inputHandler(setIno, event.target.value)}
            />
          </InputGroup>
        </Col>
      </FormGroup>
    </>
  );
};
